import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { auth, requestNotificationPermission } from '../config/firebase';

// Set base URL for axios
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AuthContext = createContext();

// Flag to track if logout is in progress
let isLoggingOut = false;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export function to check logout status
export const getIsLoggingOut = () => isLoggingOut;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState(null);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log(' Auth state changed. Firebase user:', firebaseUser?.email || 'none');
      
      if (firebaseUser) {
        setFirebaseUser(firebaseUser);
        const token = await firebaseUser.getIdToken();
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Load or create user in backend
        const loadedUser = await loadUser();
        
        // If user doesn't exist in backend, create them
        if (!loadedUser) {
          console.log('User not found in backend, creating...');
          await createUserInBackend(firebaseUser);
          await loadUser();
        }
        
        // Request notification permission
        const fcmToken = await requestNotificationPermission();
        if (fcmToken) {
          await updateFCMToken(fcmToken);
        }
      } else {
        console.log('No Firebase user, clearing state');
        setFirebaseUser(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
      }
      
      console.log('Setting loading to false');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load user from backend
  const loadUser = async () => {
    try {
      console.log('Loading user from backend...');
      const res = await axios.get('/api/auth/me');
      console.log('User loaded from backend:', res.data.data);
      setUser(res.data.data);
      return res.data.data;
    } catch (error) {
      console.error('Error loading user:', error.response?.status, error.response?.data);
      setUser(null);
      return null;
    }
  };

  // Create user in backend
  const createUserInBackend = async (firebaseUser, name) => {
    try {
      const token = await firebaseUser.getIdToken();
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await axios.post('/api/auth/register', {
        name: name || firebaseUser.displayName,
        email: firebaseUser.email,
        firebaseUid: firebaseUser.uid
      });
    } catch (error) {
      console.error('Error creating user in backend:', error);
    }
  };

  // Update FCM token
  const updateFCMToken = async (fcmToken) => {
    try {
      await axios.put('/api/notifications/fcm-token', { fcmToken });
    } catch (error) {
      console.error('Error updating FCM token:', error);
    }
  };

  // Register user
  const register = async (formData) => {
    try {
      console.log(' Starting registration process...');
      
      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      console.log(' Firebase user created:', userCredential.user.email);
      
      // Get Firebase token
      const token = await userCredential.user.getIdToken();
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Create user in MongoDB
      console.log(' Creating user in MongoDB...');
      await createUserInBackend(userCredential.user, formData.name);
      console.log(' MongoDB user created');
      
      // Sign out - user must login separately for security
      await signOut(auth);
      console.log(' User signed out, registration complete');
      
      toast.success('Account created! Please login to continue.');
      return true;
    } catch (error) {
      console.error(' Registration error:', error);
      toast.error(error.message || 'Registration failed');
      return false;
    }
  };

  // Login user
  const login = async (formData) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      
      toast.success('Login successful!');
      return true;
    } catch (error) {
      toast.error(error.message || 'Login failed');
      return false;
    }
  };

  // Logout user
  const logout = async () => {
    try {
      isLoggingOut = true;
      await signOut(auth);
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setFirebaseUser(null);
      isLoggingOut = false;
      toast.success('Logged out successfully');
    } catch (error) {
      if (!isLoggingOut) {
        toast.error('Logout failed');
      }
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const value = {
    user,
    firebaseUser,
    loading,
    register,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
